import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Shield, Wallet, BarChart3, TrendingUp, TrendingDown, DollarSign, AlertTriangle, RefreshCw, Trash2, LineChart } from 'lucide-react';
import Navbar from '../components/Navbar';
import DeleteTranchesModal from '../components/DeleteTranchesModal';
import TradingViewWidget from '../components/TradingViewWidget';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import EnrichedTranchesCards from '../components/EnrichedTranchesCards';
import { mockPortfolios, mockStockDetails, mockUser } from '../mock';
import { getStockDetail, getEnrichedTranches, addNote, getPortfolio, updateStoploss, deletePosition, deleteTranches, resolveSecurities } from '../services/api';
import { formatResidualRisk, getResidualRiskCardTheme } from '../utils/formatters';
import { analytics } from '../lib/analytics';
import type { TimelineEvent, PositionDetail, EnrichedBuyTranche, Portfolio } from '../types';
import { TimelineEventType, getBrokerColors, CategoryTimeframe, TransactionType } from '../types';

const StockDetailPage: React.FC = () => {
  const { portfolioId, stockId } = useParams();
  const navigate = useNavigate();
  const [portfolios] = useState(mockPortfolios);
  const [user] = useState(mockUser);
  
  // Stock data state
  const [stockData, setStockData] = useState<PositionDetail | null>(null);
  const [enrichedTranches, setEnrichedTranches] = useState<EnrichedBuyTranche[]>([]);
  const [resolvedTicker, setResolvedTicker] = useState<string | null>(null);  // Ticker from price DB for TradingView
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTranches, setIsLoadingTranches] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useBackendData, setUseBackendData] = useState(true);
  
  // Portfolio state (can come from backend or mock)
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  
  // UI state
  const [stoploss, setStoploss] = useState<number>(0);
  const [originalStoploss, setOriginalStoploss] = useState<number>(0); // Track original to detect changes
  const [isAddNoteOpen, setIsAddNoteOpen] = useState<boolean>(false);
  const [newNoteContent, setNewNoteContent] = useState<string>('');
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteContent, setEditNoteContent] = useState<string>('');
  
  // Delete modals state
  const [isDeletePositionOpen, setIsDeletePositionOpen] = useState(false);
  const [isDeleteTranchesOpen, setIsDeleteTranchesOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch portfolio and stock detail from backend
  useEffect(() => {
    const fetchData = async () => {
      if (!portfolioId || !stockId) return;

      setIsLoading(true);
      setError(null);

      try {
        // Fetch portfolio first
        const portfolioResponse = await getPortfolio(portfolioId);
        if (portfolioResponse.success && portfolioResponse.data) {
          setPortfolio(portfolioResponse.data);
        } else {
          // Fallback to mock portfolio
          const mockPortfolio = portfolios.find(p => p.id === portfolioId);
          if (mockPortfolio) {
            setPortfolio(mockPortfolio);
          } else {
            // Create a minimal portfolio for display
            setPortfolio({
              id: portfolioId,
              userId: 'user-1',
              name: 'Portfolio',
              totalSize: 500000,
              currentValue: 500000,
              totalPnL: 0,
              totalPnLPercentage: 0,
              lastUpdated: 'Just now',
              categories: [
                { name: 'Core', percentage: 70, timeframe: CategoryTimeframe.MONTHLY, color: '#4169E1', description: '' },
                { name: 'Satellite', percentage: 30, timeframe: CategoryTimeframe.WEEKLY, color: '#10B981', description: '' }
              ],
              brokers: []
            });
          }
        }

        // Fetch stock detail
        const response = await getStockDetail(portfolioId, stockId);
        
        if (response.success && response.data) {
          setStockData(response.data);
          setTimeline(response.data.timeline || []);
          const sl = response.data.stoploss || 0;
          setStoploss(sl);
          setOriginalStoploss(sl);
          setUseBackendData(true);
          
          // Resolve ISIN to get ticker for TradingView
          if (response.data.isin) {
            const resolveResponse = await resolveSecurities([response.data.isin], []);
            if (resolveResponse.success && resolveResponse.data?.resolved?.length > 0) {
              setResolvedTicker(resolveResponse.data.resolved[0].ticker);
            }
          }
        } else {
          // Fallback to mock data
          console.log('Using mock data for stock detail');
          const mockData = mockStockDetails[stockId];
          if (mockData) {
            setStockData(mockData);
            setTimeline(mockData.timeline || []);
            setStoploss(mockData.stoploss || 0);
          }
          setUseBackendData(false);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        // Fallback to mock data
        const mockPortfolio = portfolios.find(p => p.id === portfolioId);
        if (mockPortfolio) {
          setPortfolio(mockPortfolio);
        }
        const mockData = mockStockDetails[stockId || ''];
        if (mockData) {
          setStockData(mockData);
          setTimeline(mockData.timeline || []);
          setStoploss(mockData.stoploss || 0);
        }
        setUseBackendData(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [portfolioId, stockId, portfolios]);

  // Helper function to transform basic tranches to enriched format
  const transformToEnrichedTranches = (stockDetail: PositionDetail): EnrichedBuyTranche[] => {
    const basicTranches = stockDetail.tranches;
    const stoplossHistory = stockDetail.stoplossHistory || [];
    
    const buyTranches = basicTranches.filter(t => t.type === TransactionType.BUY);
    const sellTranches = basicTranches.filter(t => t.type === TransactionType.SELL);
    
    // Sort by timestamp
    buyTranches.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    sellTranches.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    
    // Create a map of tranche ID to stoploss
    const trancheStoplossMap: Record<string, number> = {};
    stoplossHistory.forEach(sh => {
      if (sh.trancheId) {
        trancheStoplossMap[sh.trancheId] = sh.newStoploss;
      }
    });
    
    // FIFO matching
    let remainingSells = [...sellTranches];
    
    return buyTranches.map((buy) => {
      let quantitySold = 0;
      let realizedPnL = 0;
      const exitExecutions: Array<{
        exitDate: string;
        qtySold: number;
        sellPrice: number;
        exitValue: number;
        realizedPnL: number;
        rMultiple: number;
        exitReason: string;
        sellTrancheId: string;
      }> = [];
      
      // Get stoploss for this tranche (use Position's stoploss as fallback)
      const trancheStoploss = trancheStoplossMap[buy.id] || stockDetail.stoploss || buy.price * 0.9;
      
      // Match sells to this buy tranche (FIFO)
      let remainingBuyQty = buy.quantity;
      const updatedSells: typeof remainingSells = [];
      
      for (const sell of remainingSells) {
        if (remainingBuyQty <= 0) {
          updatedSells.push(sell);
          continue;
        }
        
        const qtyToMatch = Math.min(remainingBuyQty, sell.quantity);
        if (qtyToMatch > 0) {
          const pnl = (sell.price - buy.price) * qtyToMatch;
          quantitySold += qtyToMatch;
          realizedPnL += pnl;
          remainingBuyQty -= qtyToMatch;
          
          // Calculate R-Multiple using original stoploss
          const initialRisk = buy.price - trancheStoploss;
          const rMultiple = initialRisk > 0 ? (sell.price - buy.price) / initialRisk : 0;
          
          exitExecutions.push({
            exitDate: sell.timestamp,
            qtySold: qtyToMatch,
            sellPrice: sell.price,
            exitValue: sell.price * qtyToMatch,
            realizedPnL: Math.round(pnl),
            rMultiple: Math.round(rMultiple * 10) / 10,
            exitReason: `Sold ${qtyToMatch} shares at ₹${sell.price}`,
            sellTrancheId: sell.id
          });
          
          // Update remaining sell quantity
          const remainingQty = sell.quantity - qtyToMatch;
          if (remainingQty > 0) {
            updatedSells.push({ ...sell, quantity: remainingQty });
          }
        } else {
          updatedSells.push(sell);
        }
      }
      
      remainingSells = updatedSells;
      
      const quantityOpen = buy.quantity - quantitySold;
      const currentPrice = stockDetail.currentPrice || buy.price * 1.1;
      const unrealizedPnL = quantityOpen * (currentPrice - buy.price);
      const residualRisk = quantityOpen > 0 ? quantityOpen * (currentPrice - trancheStoploss) : 0;
      
      const date = new Date(buy.timestamp).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
      
      return {
        trancheId: buy.id,
        date,
        orderId: buy.orderId,
        quantityBought: buy.quantity,
        quantitySold,
        quantityOpen,
        percentOpen: Math.round((quantityOpen / buy.quantity) * 100),
        avgBuyPrice: buy.price,
        invested: buy.price * buy.quantity,
        currentPrice,
        stoploss: trancheStoploss,
        realizedPnL: Math.round(realizedPnL),
        unrealizedPnL: Math.round(unrealizedPnL),
        residualRisk: Math.round(residualRisk),
        exitExecutions
      };
    });
  };

  // Fetch enriched tranches from backend
  useEffect(() => {
    const fetchEnrichedTranches = async () => {
      if (!portfolioId || !stockId) return;

      setIsLoadingTranches(true);

      try {
        const response = await getEnrichedTranches(portfolioId, stockId);
        
        if (response.success && response.data && response.data.tranches.length > 0) {
          setEnrichedTranches(response.data.tranches || []);
        } else {
          // Fallback: Transform basic tranches from stockData to enriched format
          console.log('Enriched tranches API not available, transforming basic tranches');
          if (stockData && stockData.tranches.length > 0) {
            const transformed = transformToEnrichedTranches(stockData);
            setEnrichedTranches(transformed);
          } else {
            setEnrichedTranches([]);
          }
        }
      } catch (err) {
        console.error('Error fetching enriched tranches:', err);
        // Fallback: Transform basic tranches from stockData to enriched format
        if (stockData && stockData.tranches.length > 0) {
          const transformed = transformToEnrichedTranches(stockData);
          setEnrichedTranches(transformed);
        } else {
          setEnrichedTranches([]);
        }
      } finally {
        setIsLoadingTranches(false);
      }
    };

    fetchEnrichedTranches();
  }, [portfolioId, stockId, stockData]);

  // Refresh data
  const handleRefresh = async () => {
    if (!portfolioId || !stockId) return;
    
    setIsLoading(true);
    setIsLoadingTranches(true);

    try {
      const [stockResponse, tranchesResponse] = await Promise.all([
        getStockDetail(portfolioId, stockId),
        getEnrichedTranches(portfolioId, stockId)
      ]);

      if (stockResponse.success && stockResponse.data) {
        setStockData(stockResponse.data);
        setTimeline(stockResponse.data.timeline || []);
        setStoploss(stockResponse.data.stoploss || 0);
      }

      if (tranchesResponse.success && tranchesResponse.data) {
        setEnrichedTranches(tranchesResponse.data.tranches || []);
      }
    } catch (err) {
      console.error('Error refreshing data:', err);
    } finally {
      setIsLoading(false);
      setIsLoadingTranches(false);
    }
  };

  const handleStoplossUpdate = async () => {
    if (!portfolioId || !stockId) return;
    if (stoploss === originalStoploss) return; // No change

    try {
      const response = await updateStoploss(portfolioId, stockId, stoploss);
      if (response.success === true) {
        // Track stoploss update
        analytics.trackStoplossUpdated(
          stockId,
          stockData?.ticker || '',
          originalStoploss,
          stoploss
        );
        setOriginalStoploss(stoploss); // Update original to new value
        // Refresh data to get updated residual risk calculations
        handleRefresh();
      } else if (response.success === false) {
        console.error('Failed to update stoploss:', response.error);
        alert(`Failed to update stoploss: ${response.error.message || 'Unknown error'}`);
        // Revert to original value
        setStoploss(originalStoploss);
      }
    } catch (error) {
      console.error('Error updating stoploss:', error);
      alert('An error occurred while updating stoploss');
      setStoploss(originalStoploss);
    }
  };

  const handleNoteAdd = async () => {
    if (!newNoteContent.trim() || !portfolioId || !stockId) return;

    try {
      if (useBackendData) {
        const response = await addNote(portfolioId, stockId, newNoteContent);
        if (response.success) {
          // Refresh stock data to get updated timeline
          handleRefresh();
        }
      } else {
        // Mock mode - add note locally
        const newNote: TimelineEvent = {
          id: `n${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          type: TimelineEventType.NOTE,
          content: newNoteContent,
          autoGenerated: false,
          editable: true
        };
        setTimeline([newNote, ...timeline]);
      }
    } catch (err) {
      console.error('Error adding note:', err);
    }

    setIsAddNoteOpen(false);
    setNewNoteContent('');
  };

  const handleNoteEdit = (noteId: string) => {
    const noteToEdit = timeline.find(n => n.id === noteId);
    if (noteToEdit && noteToEdit.editable) {
      setEditingNoteId(noteId);
      setEditNoteContent(noteToEdit.content);
    }
  };

  const handleNoteSave = () => {
    if (editNoteContent.trim()) {
      setTimeline(timeline.map(note => 
        note.id === editingNoteId 
          ? { ...note, content: editNoteContent }
          : note
      ));
      setEditingNoteId(null);
      setEditNoteContent('');
    }
  };

  const handleNoteCancel = () => {
    setEditingNoteId(null);
    setEditNoteContent('');
  };

  // Delete position handler
  const handleDeletePosition = async () => {
    if (!portfolioId || !stockId) return;
    
    setIsDeleting(true);
    try {
      const response = await deletePosition(portfolioId, stockId);
      if (response.success) {
        // Navigate back to portfolio after successful deletion
        navigate(`/portfolio/${portfolioId}/current`);
      } else {
        const errorMsg = 'error' in response ? response.error?.message : 'Unknown error';
        alert(`Failed to delete position: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Error deleting position:', error);
      alert('An error occurred while deleting the position');
    } finally {
      setIsDeleting(false);
      setIsDeletePositionOpen(false);
    }
  };

  // Delete tranches handler
  const handleDeleteTranches = async (trancheIds: string[]) => {
    if (!portfolioId || !stockId) return;
    
    const response = await deleteTranches(portfolioId, stockId, trancheIds);
    if (response.success) {
      // Refresh data after successful deletion
      handleRefresh();
    } else {
      const errorMsg = 'error' in response ? response.error?.message : 'Failed to delete tranches';
      throw new Error(errorMsg);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar
          portfolios={portfolios}
          selectedPortfolio={portfolioId}
          onPortfolioChange={(id) => navigate(`/portfolio/${id}/current`)}
          user={user}
        />
        <div className="max-w-[1600px] mx-auto px-6 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-48"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="grid grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <div key={i} className="h-32 bg-gray-200 rounded"></div>)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!portfolio || !stockData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar
          portfolios={portfolios}
          selectedPortfolio={portfolioId}
          onPortfolioChange={(id) => navigate(`/portfolio/${id}/current`)}
          user={user}
        />
        <div className="max-w-[1600px] mx-auto px-6 py-8 text-center">
          <p className="text-gray-600">Stock not found</p>
          <Button onClick={() => navigate(`/portfolio/${portfolioId}/current`)} className="mt-4">
            Back to Portfolio
          </Button>
        </div>
      </div>
    );
  }

  const isProfitable = stockData.unrealizedPnL >= 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar
        portfolios={portfolios}
        selectedPortfolio={portfolioId}
        onPortfolioChange={(id) => navigate(`/portfolio/${id}/current`)}
        user={user}
      />

      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Back Button & Data Source Indicator */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            className="gap-2"
            onClick={() => navigate(`/portfolio/${portfolioId}/current`)}
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            {!useBackendData && (
              <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                Using Mock Data
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={handleRefresh}
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stock Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-lg flex items-center justify-center font-bold text-white text-xl"
                style={{ backgroundColor: '#8B5CF6' }}
              >
                {stockData.ticker.substring(0, 3)}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{stockData.ticker}</h1>
                <div className="flex items-center gap-3 mt-2">
                  <p className="text-sm text-gray-500">{stockData.exchange?.name || 'NSE'}: {stockData.ticker}</p>
                  {stockData.broker && (
                    <>
                      <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                      <span className={`text-xs px-2 py-1 rounded ${getBrokerColors(stockData.broker).bg} ${getBrokerColors(stockData.broker).text}`}>
                        {stockData.broker}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select defaultValue={stockData.category}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {portfolio.categories.map(cat => (
                    <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                className="gap-2 text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                onClick={() => setIsDeletePositionOpen(true)}
              >
                <Trash2 className="w-4 h-4" />
                Delete Position
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <Wallet className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-900">₹{stockData.investedAmount.toLocaleString('en-IN')}</p>
            <p className="text-xs text-blue-700">Invested Amount</p>
            <p className="text-xs text-blue-600 mt-1">{((stockData.investedAmount / 245680) * 100).toFixed(1)}% of portfolio</p>
          </Card>

          <Card className="p-4 bg-green-50 border-green-200">
            <div className="flex items-center justify-between mb-2">
              <BarChart3 className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-900">
              {stockData.currentValue !== null ? `₹${stockData.currentValue.toLocaleString('en-IN')}` : 'N/A'}
            </p>
            <p className="text-xs text-green-700">Current Value</p>
            <p className="text-xs text-green-600 mt-1">
              {stockData.unrealizedPnLPercent !== null ? `${stockData.unrealizedPnLPercent >= 0 ? '+' : ''}${stockData.unrealizedPnLPercent}% overall` : ''}
            </p>
          </Card>

          <Card className="p-4 bg-gray-50 border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-5 h-5 text-gray-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">₹{stockData.avgBuyPrice}</p>
            <p className="text-xs text-gray-700">Avg Buy Price</p>
            <p className="text-xs text-gray-600 mt-1">{(stockData.investedAmount / stockData.avgBuyPrice).toFixed(1)} shares total</p>
          </Card>

          <Card className="p-4 bg-purple-50 border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-900">
              {stockData.currentPrice !== null ? `₹${stockData.currentPrice}` : 'N/A'}
            </p>
            <p className="text-xs text-purple-700">Current Price</p>
            <p className="text-xs text-purple-600 mt-1">{stockData.currentPrice !== null ? '+2.1% today' : ''}</p>
          </Card>

          <Card className="p-4 bg-emerald-50 border-emerald-200">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="text-2xl font-bold text-emerald-900">
              {stockData.unrealizedPnL !== null 
                ? `${stockData.unrealizedPnL >= 0 ? '+' : ''}₹${stockData.unrealizedPnL.toLocaleString('en-IN')}`
                : 'N/A'}
            </p>
            <p className="text-xs text-emerald-700">Unrealized P&L</p>
            <p className="text-xs text-emerald-600 mt-1">
              {stockData.unrealizedPnLPercent !== null 
                ? `${stockData.unrealizedPnLPercent >= 0 ? '+' : ''}${stockData.unrealizedPnLPercent}% gain`
                : ''}
            </p>
          </Card>

          <Card className="p-4 bg-orange-50 border-orange-200">
            <div className="flex items-center justify-between mb-2">
              <TrendingDown className="w-5 h-5 text-orange-600" />
            </div>
            <p className="text-2xl font-bold text-orange-900">+₹{stockData.realizedPnL.toLocaleString('en-IN')}</p>
            <p className="text-xs text-orange-700">Realized P&L</p>
            <p className="text-xs text-orange-600 mt-1">From sold positions</p>
          </Card>

          <Card className={`p-4 ${getResidualRiskCardTheme(stockData.residualRisk)}`}>
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className={`w-5 h-5 ${formatResidualRisk(stockData.residualRisk).colorClass}`} />
            </div>
            <p className={`text-2xl font-bold ${formatResidualRisk(stockData.residualRisk).colorClass}`}>
              {formatResidualRisk(stockData.residualRisk).value}
            </p>
            <p className={`text-xs ${formatResidualRisk(stockData.residualRisk).colorClass}`}>Residual Risk</p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-red-600">SL:</p>
              <Input
                type="number"
                value={stoploss}
                onChange={(e) => setStoploss(Number(e.target.value))}
                onBlur={handleStoplossUpdate}
                onWheel={(e) => e.currentTarget.blur()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleStoplossUpdate();
                  }
                  // Prevent arrow keys from changing value
                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    e.preventDefault();
                  }
                }}
                className="h-6 w-20 text-xs border-red-300"
              />
            </div>
          </Card>
        </div>

        {/* TradingView Chart Section */}
        {resolvedTicker && (
          <Card className="p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <LineChart className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Price Chart</h2>
            </div>
            <div className="h-[650px] rounded-lg overflow-hidden border border-gray-200">
              <TradingViewWidget
                symbol={resolvedTicker}
                theme="light"
              />
            </div>
          </Card>
        )}

        {/* Enriched Investment Tranches - Card-based layout */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Investment Tranches</h2>
            {stockData.tranches && stockData.tranches.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2 text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                onClick={() => setIsDeleteTranchesOpen(true)}
              >
                <Trash2 className="w-4 h-4" />
                Delete Tranches
              </Button>
            )}
          </div>
          <EnrichedTranchesCards 
            tranches={enrichedTranches} 
            isLoading={isLoadingTranches}
            showHeader={false}
          />
        </div>

        {/* Timeline Notes Section */}
        <Card className="p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">Investment Timeline & Notes</h2>
            </div>
            <Button 
              className="gap-2 bg-emerald-600 hover:bg-emerald-700" 
              size="sm"
              onClick={() => setIsAddNoteOpen(true)}
            >
              <Plus className="w-4 h-4" />
              Add Note
            </Button>
          </div>

          {/* Timeline */}
          <div className="space-y-6">
            {timeline?.sort((a, b) => {
              // Sort by createdAt if available (precise), otherwise by date
              const timeA = a.createdAt ? new Date(a.createdAt).getTime() : new Date(a.date).getTime();
              const timeB = b.createdAt ? new Date(b.createdAt).getTime() : new Date(b.date).getTime();
              return timeB - timeA;  // Descending order (newest first)
            }).map((entry, index) => (
              <div key={entry.id} className="flex gap-4">
                {/* Timeline line and dot */}
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    entry.type === TimelineEventType.BUY ? 'bg-green-100' : 
                    entry.type === TimelineEventType.SELL ? 'bg-red-100' : 
                    'bg-blue-100'
                  }`}>
                    {entry.type === TimelineEventType.BUY ? (
                      <TrendingUp className={`w-5 h-5 text-green-600`} />
                    ) : entry.type === TimelineEventType.SELL ? (
                      <TrendingDown className={`w-5 h-5 text-red-600`} />
                    ) : (
                      <Edit2 className={`w-5 h-5 text-blue-600`} />
                    )}
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 h-full bg-gray-200 mt-2"></div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 pb-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${
                        entry.type === TimelineEventType.BUY ? 'bg-green-100 text-green-700' : 
                        entry.type === TimelineEventType.SELL ? 'bg-red-100 text-red-700' : 
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {entry.type.toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-500">
                        {new Date(entry.date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })} at {entry.time}
                      </span>
                    </div>
                    {entry.editable && editingNoteId !== entry.id && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="gap-1 h-7 text-xs"
                        onClick={() => handleNoteEdit(entry.id)}
                      >
                        <Edit2 className="w-3 h-3" />
                        Edit
                      </Button>
                    )}
                  </div>
                  {editingNoteId === entry.id ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editNoteContent}
                        onChange={(e) => setEditNoteContent(e.target.value)}
                        className="min-h-[100px] resize-none text-sm"
                      />
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs"
                          onClick={handleNoteSave}
                        >
                          Save
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-7 text-xs"
                          onClick={handleNoteCancel}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-700 leading-relaxed">{entry.content}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {timeline?.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Edit2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No notes yet. Start documenting your investment journey!</p>
            </div>
          )}
        </Card>
      </div>

      {/* Add Note Dialog */}
      <Dialog open={isAddNoteOpen} onOpenChange={setIsAddNoteOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Investment Note</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Document your investment rationale, market analysis, or any observations about this position..."
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              className="min-h-[200px] resize-none"
            />
            <p className="text-xs text-gray-500 mt-2">
              This note will be added to your investment timeline with today's date and time.
            </p>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsAddNoteOpen(false);
                setNewNoteContent('');
              }}
            >
              Cancel
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleNoteAdd}
            >
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Position Confirmation Dialog */}
      <Dialog open={isDeletePositionOpen} onOpenChange={setIsDeletePositionOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Position
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to permanently delete this position?
            </p>
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 mb-4">
              <p className="font-semibold text-gray-900">{stockData.ticker}</p>
              <p className="text-sm text-gray-500">{stockData.availableQuantity} shares</p>
            </div>
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> This will permanently delete the position along with all its tranches, 
                notes, and timeline events. This action cannot be undone.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDeletePositionOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeletePosition}
              disabled={isDeleting}
              className="gap-2"
            >
              {isDeleting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Delete Position
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Tranches Modal */}
      {stockData.tranches && (
        <DeleteTranchesModal
          isOpen={isDeleteTranchesOpen}
          onClose={() => setIsDeleteTranchesOpen(false)}
          tranches={stockData.tranches}
          onConfirmDelete={handleDeleteTranches}
          stockName={stockData.ticker}
        />
      )}
    </div>
  );
};

export default StockDetailPage;
